import React from "react";
import Panel from "../../components/panels/panel";
import { useApi, useManualPagination } from "../../utilities/hookutils";
import ESignParticipantProgressElements from "../../components/Document/ESignParticipantProgress";
import { NoDataAvailableDiv } from "../../components/NoDataAvailableDiv";
import ESignAgencyStatisticsDiv from "./ESignAgencyStatisticsDiv";
import DocumentLifeCyclePill from "../../components/Document/DocumentLifeCyclePill";
import LinkedShowsList from "components/LinkedShows/LinkedShowsList";
import BaseReactSelectDropdown from "components/dropdowns/reactselect/BaseReactSelectDropdown";

interface Props {
  config: server.dto.DashboardESignForAgencyPanelConfig
}

interface Option {
  label: string
  value: server.dto.ESignAgencyDashboardOptions
};
const options: Option[] = [
  {
    label: RESX.ESign.SignedBySigner,
    value: "recentlySignedBySigner"
  },
  {
    label: RESX.ESign.SignedBySender,
    value: "recentlySignedBySender"
  },
  {
    label: RESX.ESign.WaitingForSigner,
    value: "waitingForSigner"
  },
  {
    label: RESX.ESign.WaitingForSender,
    value: "waitingForSender"
  }
];

const ESignAgencyPanel = (props: Props) => {
  const maxPerPage = 8;

  const [filter, setFilter] = React.useState<Option>(options[0]);
  const [instruction, onInstructionChange] = React.useState<server.dto.ListESignAgencyDashboardInstruction>({
    documentType: "contract", // We want to extract this into some sort of type picker
    filter: filter.value,
    page: 1,
    perPage: maxPerPage
  });

  const api = useApi(
    () => window.CoreApi.ESignAgencyDashboard.Dashboard(instruction),
    ["ESignAgencyDashboard", instruction],
  );

  const paginate = async (paginationData: server.dto.PaginationData) => {
    onInstructionChange({ ...instruction, page: paginationData.page, perPage: paginationData.perPage });
  };

  const pagination = useManualPagination(instruction.page || 1, maxPerPage, paginate);

  // Reloads page when on initial load or when filter changes
  React.useEffect(() => {
    onInstructionChange({
      ...instruction,
      filter: filter.value,
      page: 1
    });
  }, [filter]);

  const data = api.data?.data ?? null;

  return <Panel
    isLoading={api.isLoading}
    hasData={data != null}
    panelClassName="s1_dashboard-panel"
    contentCssClass="flex-column gap-big"
  >
    <div className="flex">
      <div className="grow1">
        <h2>{props.config.headerLabel}</h2>
      </div>
      <div>
        <BaseReactSelectDropdown
          variant="borderless"
          options={options}
          value={filter}
          onChange={(value) => { setFilter(value); }}
          getLabel={(option) => option.label}
          getValue={(option) => option.value}
          excludeKeys={[]}
          config={{}}
        />
      </div>
    </div>

    <div className="divider-line" />

    {data && <ESignAgencyStatisticsDiv value={data} />}

    {data?.list.length === 0 && <div className="margin-top-large"><NoDataAvailableDiv /></div>}

    {data?.list.length > 0 && <div className="list-with-gaps line-layout">
      <div className="list-with-gaps-body">
        {data?.list.map((d) => <div key={d.documentId} className="flex">
          <div style={{ flex: "1 1 0" }} className="flex-column gap-small">
            <div>
              <a href={d.detailUrl} className="table-main-link">
                {d.documentNumber}
              </a>
            </div>

            <div style={{ flex: "1 1 0" }} className="text-sublabel">
              {d.addressee.name}
            </div>

            <div>
              <DocumentLifeCyclePill value={d.lifeCycle.value} overDue={d.overDue} />
            </div>
          </div>
          <div style={{ flex: "1.5 1 0" }}>
            <LinkedShowsList shows={d.linkedShows} />
          </div>

          <div>
            <ESignParticipantProgressElements sender={d.senderProgress.value} signer={d.signerProgress.value} />
          </div>
        </div>)}
      </div>
    </div>}

    {data?.list.length > 0 && <div>
      <div className="flex justify-end margin-bottom">
        {pagination.Component}
      </div>
    </div>}

  </Panel >;
};

export default ESignAgencyPanel;